import React, { useCallback, useEffect, useRef, useState } from 'react';
import { parseInventoryQuantity, MAX_INVENTORY_QUANTITY } from '../../../../utils/inventoryInputs';

/**
 * Кількість як текст; подвійний клік → інпут (значення виділене).
 * Збереження: Enter або клік будь-де поза зоною інпута (mousedown, capture).
 */
const IncomingShipmentQuantityField = ({
  itemId,
  quantity,
  saving,
  onSave,
  className = '',
  inputClassName = 'form-control form-control-sm'
}) => {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(String(quantity));
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const localRef = useRef(String(quantity));
  const quantityRef = useRef(quantity);

  useEffect(() => {
    localRef.current = local;
  }, [local]);

  useEffect(() => {
    quantityRef.current = quantity;
  }, [quantity]);

  useEffect(() => {
    if (!editing) {
      setLocal(String(quantity));
    }
  }, [quantity, itemId, editing]);

  useEffect(() => {
    if (saving && editing) {
      setLocal(String(quantity));
      setEditing(false);
    }
  }, [saving, editing, quantity]);

  const tryCommit = useCallback(() => {
    const n = parseInventoryQuantity(localRef.current);
    const q = quantityRef.current;
    if (n == null) {
      setLocal(String(q));
      return;
    }
    if (n !== q) {
      onSave(n);
    }
  }, [onSave]);

  const closeEditor = useCallback(() => {
    setEditing(false);
  }, []);

  const commitAndClose = useCallback(() => {
    tryCommit();
    closeEditor();
  }, [tryCommit, closeEditor]);

  useEffect(() => {
    if (!editing) return;
    const onDocMouseDown = (e) => {
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      commitAndClose();
    };
    document.addEventListener('mousedown', onDocMouseDown, true);
    return () => document.removeEventListener('mousedown', onDocMouseDown, true);
  }, [editing, commitAndClose]);

  useEffect(() => {
    if (!editing) return;
    const id = window.requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.select();
    });
    return () => window.cancelAnimationFrame(id);
  }, [editing]);

  const startEdit = () => {
    if (saving) return;
    setLocal(String(quantity));
    setEditing(true);
  };

  if (editing) {
    return (
      <div ref={wrapRef} className={'d-inline-flex align-items-center' + (className ? ` ${className}` : '')}>
        <input
          ref={inputRef}
          type='number'
          min={1}
          max={MAX_INVENTORY_QUANTITY}
          step={1}
          className={inputClassName}
          style={{ width: '4.25rem' }}
          value={local}
          disabled={saving}
          onChange={(e) => setLocal(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitAndClose();
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              setLocal(String(quantityRef.current));
              closeEditor();
            }
          }}
        />
      </div>
    );
  }

  return (
    <span
      className={
        'font-weight-bold d-inline-block ' + (saving ? 'text-muted ' : '') + (className || '')
      }
      style={{ cursor: saving ? 'not-allowed' : 'pointer', minWidth: '1.5rem', userSelect: 'none' }}
      title='Подвійний клік, щоб змінити кількість'
      onDoubleClick={startEdit}
      aria-label={`Кількість ${quantity}, подвійний клік для зміни`}
    >
      {quantity}
    </span>
  );
};

export default IncomingShipmentQuantityField;

